import { HorizontalOriginOption, InlineAlertType, InlineAlertWidget, TextLinkWidget, VerticalOriginOption } from '@tableau/widgets';
import { TooltipInfoIcon as InfoIcon } from '@tableau/widgets';
import React, { Component } from 'react';
import _ from 'underscore';

import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Messages } from '~src/codegen/Localize';
import { ButtonWithUberDialog } from '~src/commonInfra/components/ButtonWithUberDialog';
import { openReactActionDialog, ReactActionDialogBodyProps } from '~src/commonInfra/dialogs/ReactActionDialog';
import { HelpService } from '~src/commonInfra/services/HelpService';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { ResultHelper } from '~src/httpClient/utils/ResultHelper';
import WebHttpClient from '~src/httpClient/WebHttpClient';
import { WebHttpClientWithErrorToastInstance } from '~src/httpClient/WebHttpClientWithErrorToast';
import { requestSpaceContents } from '~src/spacesPlatform/actions/RequestSpaceContentsActions';
import { cssClass } from '~src/style';

import { GetResourceGovernanceServerSettingsRequest, IResourceGovernanceServerSettings, IResourceGovernanceServerSettingsKey, IResourceGovernanceSettingsKey, UpdateResourceGovernanceServerSettingsRequest } from '../types/ResourceGovernance';
import { SiteLimitsDialogLinkToken, SiteLimitsErrorLinkToken } from './ResourceGovernanceEditSiteLimitsDialog';
import { ConcurrentJobLimitRowProps, ResourceGovernanceLimitsRow, rowShouldAlert } from './ResourceGovernanceLimitsRow';

export const DefaultLimitsDialogLinkTestId = 'default-dialog-learn-more';
export const cssClassPrefix = 'resource-governance-dialog-edit-default';
const leftPadClass = cssClass(cssClassPrefix, 'left-pad', {
  paddingLeft: 7,
  verticalAlign: 'middle',
  display: 'flex',
  justifyContent: 'center',
  minWidth: 180,
});
const flexApart = cssClass(cssClassPrefix, 'flex-apart', {
  paddingLeft: 7,
  verticalAlign: 'middle',
  display: 'flex',
  justifyContent: 'space-between',
  minWidth: 100,
});
const tooltipStyle = cssClass(cssClassPrefix, 'tooltip', {
  whiteSpace: 'pre-line',
});
const rowKeys: IResourceGovernanceSettingsKey[] = ['extractsConcurrencyLimit', 'subscriptionsConcurrencyLimit', 'flowsConcurrencyLimit', 'runtimeLimit'];
export type ResourceGovernanceEditDefaultLimitsDialogProps = {
  updateState?: (newSettings: IResourceGovernanceServerSettings) => void;
  title: string;
  isFirstRun: boolean;
} & IResourceGovernanceServerSettings;

export type ReactActionResourceGovernanceEditDefaultLimitsDialogProps = ReactActionDialogBodyProps<ResourceGovernanceEditDefaultLimitsDialogProps, IResourceGovernanceServerSettings>;
export class ResourceGovernanceDefaultLimitsDialog extends Component<ReactActionResourceGovernanceEditDefaultLimitsDialogProps> {

  constructor(props: ReactActionResourceGovernanceEditDefaultLimitsDialogProps) {
    super(props);
    this.updateDialog = this.updateDialog.bind(this);
  }

  updateDialog = (key: IResourceGovernanceSettingsKey) => (value: string) => {
    const isValidNum = /^\d+\.?\d*$/.test(value);
    value === undefined
      ? this.props.updateDialogState({ [key]: value })
      : this.props.updateDialogState({ [key]: isValidNum ? Number(value) : NaN });
  };

  public render() {
    const headerRow = <tr key='header'>
      <td style={{maxWidth: 185}}><b>{Messages.resourceGovernanceConcurrentJobsLimitsHeader()}</b></td>
      <td><b className={leftPadClass}>{Messages.resourceGovernanceNoLimitValue()}</b></td>
      <td className={flexApart}>
        <b style={{ display: 'flex', alignSelf: 'center' }}>{Messages.resourceGovernanceConcurrencyDeaultLimitsHeader()}</b>
        {<ButtonWithUberDialog
          renderButton={showDialog => <InfoIcon onClick={showDialog} />}
          dialogWidth={260}
          uberDialogContent={<span className={tooltipStyle}>
            {Messages.resourceGovernanceConcurrencyLimitsTooltip()}
            <TextLinkWidget testId={DefaultLimitsDialogLinkTestId}
              href={HelpService.getServerHelpUrlByCategory(SiteLimitsDialogLinkToken)}
              target={'_blank'}>
              {Messages.resourceGovernanceLearnMoreMessage()}
            </TextLinkWidget>
          </span>}
          anchorOrigin={{ horizontal: HorizontalOriginOption.right, vertical: VerticalOriginOption.top }}
          stackedOrigin={{ horizontal: HorizontalOriginOption.left, vertical: VerticalOriginOption.top }}
        />}
      </td>
    </tr>;
    const rowsInfo: ConcurrentJobLimitRowProps[] = [
      {
        limitType: Messages.resourceGovernanceConcurrencySubscriptions(),
        update: this.updateDialog('subscriptionsConcurrencyLimit'),
        value: this.props.dialogState.subscriptionsConcurrencyLimit,
        originalValue: this.props.subscriptionsConcurrencyLimit,
        description: Messages.resourceGovernanceConcurrencyDescription(),
        testId: 'subscriptionsConcurrencyLimit',
      },
      {
        limitType: Messages.resourceGovernanceConcurrencyExtractRefreshes(),
        update: this.updateDialog('extractsConcurrencyLimit'),
        value: this.props.dialogState.extractsConcurrencyLimit,
        originalValue: this.props.extractsConcurrencyLimit,
        description: Messages.resourceGovernanceConcurrencyDescription(),
        testId: 'extractsConcurrencyLimit',
      },
      {
        limitType: Messages.resourceGovernanceConcurrencyFlows(),
        update: this.updateDialog('flowsConcurrencyLimit'),
        value: this.props.dialogState.flowsConcurrencyLimit,
        originalValue: this.props.flowsConcurrencyLimit,
        description: Messages.resourceGovernanceConcurrencyDescription(),
        testId: 'flowsConcurrencyLimit',
      }];
    const concurrentLimitRows = rowsInfo.map(row => <ResourceGovernanceLimitsRow key={row.limitType} {...row} />);
    const anyRowAlerting = _.any(rowsInfo, row => rowShouldAlert(row.value, row.originalValue));
    const alertExplanation = <InlineAlertWidget
      testId='decreased-limit-alert'
      type={InlineAlertType.Warning}
    >
      {Messages.resourceGovernanceConcurrencyDefaultDecreasedLimitAlert()}
    </InlineAlertWidget>;
    const dailyLimitHeader = <tr><td style={{ paddingTop: 12 }}><b>{Messages.resourceGovernanceDailyLimitsHeader()}</b></td></tr>;
    const runTimeLimitRowProps: ConcurrentJobLimitRowProps = {
      limitType: Messages.resourceGovernanceBackgrounderHours(),
      update: this.updateDialog('runtimeLimit'),
      value: this.props.dialogState.runtimeLimit,
      originalValue: this.props.runtimeLimit,
      description: Messages.resourceGovernanceBackgrounderDescription(),
      testId: 'runtimeLimit'
    };

    return <>
      <table><tbody>
        {headerRow}
        {concurrentLimitRows}
        {dailyLimitHeader}
        <ResourceGovernanceLimitsRow {...runTimeLimitRowProps} />
      </tbody></table>
      {anyRowAlerting && alertExplanation}
    </>;
  }
}

export function saveDefaultLimitsSettings(props: ResourceGovernanceEditDefaultLimitsDialogProps, state: IResourceGovernanceServerSettings): Promise<any> {
  const updatedSettings = Object.keys(state).map((key: IResourceGovernanceServerSettingsKey) => {
    const value = state[key] === undefined ? null : state[key];
    return { [key]: value };
  });
  const flattenedSettings: IResourceGovernanceServerSettings = _.assign({}, ...updatedSettings) as { [K in IResourceGovernanceServerSettingsKey]: number | null };

  return WebHttpClient.sendRequest(new UpdateResourceGovernanceServerSettingsRequest({ resourceGovernanceServerSettings: flattenedSettings }))
    .then((result: IResult) => {
      if (ResultHelper.hasErrors(result)) {
        return Promise.reject();
      }
    })
    .then(() => {
      ToasterService.instance.pop({ type: ToastType.Success, text: Messages.resourceGovernanceServerSettingsSaveSuccessful() });
      props.updateState && props.updateState(state);
      getAppStore().dispatch(requestSpaceContents());
    })
    .catch(() => {
      ToasterService.instance.pop({
        type: ToastType.Error,
        text: Messages.resourceGovernanceServerSettingsSaveFailed(),
        actionMessage: Messages.resourceGovernanceLearnMoreMessage(),
        actionHref: HelpService.getServerHelpUrlByCategory(SiteLimitsErrorLinkToken)
      });
    });
}

export function actionDisabled(props: ReactActionResourceGovernanceEditDefaultLimitsDialogProps, state: IResourceGovernanceServerSettings): boolean {
  const rowUnchanged = rowKeys.map(key => {
    const dialogValue = state[key];
    const dialogInitialValue = props[key];
    return dialogValue === dialogInitialValue;
  });
  const rowInvalid = rowKeys.map(key => {
    const dialogValue = state[key];
    // Technically we never expect to see an infinite value here, but better safe than sorry
    const invalidValue = dialogValue !== undefined && (isNaN(dialogValue) || !isFinite(dialogValue));
    return invalidValue;
  });
  // We can update if it's the first run or if any of the rows were changed
  const canUpdate = props.isFirstRun || !_.all(rowUnchanged);
  // Unless the values would be invalid, in which case it's never ok to update
  return !canUpdate || _.any(rowInvalid) ;
}

export interface OpenResourceGovernanceEditDefaultLimitsDialogParams {
  isFirstRun: boolean;
  updateState?: (newSettings: IResourceGovernanceServerSettings) => void;
}

export async function openResourceGovernanceEditDefaultLimitsDialog(params: OpenResourceGovernanceEditDefaultLimitsDialogParams) {
  const buttonText = params.isFirstRun
    ? Messages.resourceGovernanceSetDefaultTitle()
    : Messages.resourceGovernanceEditDefaultTitle();
  const serverSettingsResult = await WebHttpClientWithErrorToastInstance.sendRequest(new GetResourceGovernanceServerSettingsRequest());
  if (serverSettingsResult.errors?.length > 0) {
    ToasterService.instance.pop({ type: ToastType.Error, text: Messages.errorUnknown() });
    return Promise.reject(serverSettingsResult.errors);
  }

  const initialDialogState: IResourceGovernanceServerSettings = {
    extractsConcurrencyLimit: serverSettingsResult.extractsConcurrencyLimit,
    subscriptionsConcurrencyLimit: serverSettingsResult.subscriptionsConcurrencyLimit,
    flowsConcurrencyLimit: serverSettingsResult.flowsConcurrencyLimit,
    runtimeLimit: serverSettingsResult.runtimeLimit,
    nextResetAt: serverSettingsResult.nextResetAt,
  };
  const initialBodyProps: ResourceGovernanceEditDefaultLimitsDialogProps = {
    ...initialDialogState,
    updateState: params.updateState,
    title: buttonText,
    isFirstRun: params.isFirstRun,
  };

  const modal = openReactActionDialog({
    bodyComponent: ResourceGovernanceDefaultLimitsDialog,
    dialogWidth: 600,
    title: buttonText,
    initialBodyProps,
    initialDialogState,
    actionName: Messages.save(),
    action: saveDefaultLimitsSettings,
    actionDisabled: actionDisabled,
    dismissDialogOnActionFailed: true,
    initiallyFocusedElement: (dialog => dialog.querySelector('input'))
  });
  return await modal.result;
}
